/**
 * Position Form Screen
 *
 * T032 [US1]: Screen for manually adding a new position to the portfolio.
 * T041 [US2]: Extended to support editing existing positions.
 * Handles form validation, duplicate symbol detection, and position creation/update.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect, type RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import type { StackNavigationProp } from '@react-navigation/stack';
import axios from 'axios';
import { PositionForm } from '../../components/portfolio/PositionForm';
import {
  useCreatePosition,
  usePosition,
  useUpdatePosition,
  useDeletePosition,
} from '../../services/api/queries/usePortfolio';
import { showSuccess, showError, showConfirm } from '../../utils/alert';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getToken } from '../../services/tokenStorage';
import { DuplicateInstrumentDialog } from '../../components/portfolio/DuplicateInstrumentDialog';
import {
  PositionReconcileModal,
  type ReconcileData,
} from '../../components/portfolio/PositionReconcileModal';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';

const API_URL = process.env.EXPO_PUBLIC_API_URL || 'https://topnote-api-sbp5f6nlfa-uc.a.run.app';

type PositionFormScreenNavigationProp = StackNavigationProp<
  PortfolioNavigatorParamList,
  'AddPosition'
>;
type PositionFormScreenRouteProp = RouteProp<PortfolioNavigatorParamList, 'AddPosition'>;

interface DuplicatePositionData {
  id: number;
  symbol: string;
  quantity: number;
  cost_price: number;
  currency?: string;
}

interface DuplicateInstrumentData {
  id: number;
  symbol: string;
  quantity: number;
  cost_price: number;
  currency?: string;
}

export function PositionFormScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<PositionFormScreenNavigationProp>();
  const route = useRoute<PositionFormScreenRouteProp>();
  const createPosition = useCreatePosition();
  const updatePosition = useUpdatePosition();
  const deletePosition = useDeletePosition();
  const { userId } = useAuth();

  // T041 [US2]: Extract positionId for edit mode
  const positionId = route.params?.positionId;
  const isEditMode = !!positionId;

  // T041 [US2]: Fetch position data when editing
  const { data: position, isLoading: isLoadingPosition } = usePosition(
    positionId!,
    isEditMode // Only fetch when in edit mode
  );

  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Ref to trigger form submit from header button
  const formSubmitRef = useRef<(() => void) | null>(null);

  // Store initial values to detect unsaved changes
  const initialValuesRef = useRef<{
    symbol?: string;
    quantity?: number;
    cost_price?: number;
    currency?: string;
    asset_type?: string;
    sector?: string | null;
    industry?: string | null;
  }>({});

  // Duplicate dialog state
  const [duplicateDialog, setDuplicateDialog] = useState<{
    visible: boolean;
    existingPosition?: DuplicatePositionData;
    formData?: {
      symbol: string;
      quantity: number;
      cost_price: number;
      currency: string;
      asset_type?: string;
      sector?: string;
      industry?: string;
    };
    duplicateType?: 'symbol' | 'instrument';
    instrumentId?: number;
  }>({ visible: false });

  // Reconcile dialog state
  const [reconcileDialog, setReconcileDialog] = useState<{
    visible: boolean;
    reconcileData: ReconcileData | null;
  }>({
    visible: false,
    reconcileData: null,
  });

  // Track asset category, sector, industry from form for duplicate dialog
  const [currentAssetCategory, setCurrentAssetCategory] = useState<string>('Stocks');
  const [currentSector, setCurrentSector] = useState<string>('');
  const [currentIndustry, setCurrentIndustry] = useState<string>('');

  // Update initial values ref when position data loads
  useEffect(() => {
    if (isEditMode && position) {
      initialValuesRef.current = {
        symbol: position.symbol,
        quantity: parseFloat(position.quantity),
        cost_price: parseFloat(position.cost_price),
        currency: position.currency,
        asset_type: position.asset_type,
        sector: position.sector,
        industry: position.industry,
      };
    } else if (!isEditMode) {
      initialValuesRef.current = {};
    }
  }, [position, isEditMode]);

  // T044 [US2]: Handle delete position with confirmation
  const handleDelete = useCallback(async () => {
    if (!isEditMode || !positionId) return;

    const confirmed = await showConfirm(
      t('portfolio.deletePosition', { defaultValue: 'Delete Position' }),
      `${t('portfolio.deleteConfirm', { defaultValue: 'Are you sure you want to delete' })} ${position?.symbol || t('portfolio.thisPosition', { defaultValue: 'this position' })}? ${t('portfolio.cannotUndo', { defaultValue: 'This action cannot be undone.' })}`,
      t('common.delete', { defaultValue: 'Delete' }),
      t('common.cancel', { defaultValue: 'Cancel' })
    );

    if (!confirmed) return;

    setLoading(true);

    try {
      await deletePosition.mutateAsync(positionId);
      showSuccess(
        t('portfolio.positionDeleted', { defaultValue: 'Position deleted successfully!' })
      );

      // After deleting a position, navigate back to Positions screen
      // Use popToTop() to go back to the root (Positions screen) instead of just goBack()
      // This prevents staying on PositionDetailScreen which no longer exists
      navigation.popToTop();
    } catch (error: any) {
      showError(
        error.message || t('portfolio.deleteFailed', { defaultValue: 'Failed to delete position' })
      );
    } finally {
      setLoading(false);
    }
  }, [isEditMode, positionId, position?.symbol, deletePosition, navigation, t]);

  // Set up header buttons with save/delete
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <View style={styles.headerButtons}>
          {/* Delete button - only in edit mode */}
          {isEditMode && (
            <TouchableOpacity
              onPress={handleDelete}
              disabled={loading}
              style={[styles.headerButton, styles.headerButtonDelete]}
              activeOpacity={0.7}
            >
              <Text style={[styles.headerButtonText, styles.headerButtonTextDelete]}>
                {t('common.delete', { defaultValue: 'Delete' })}
              </Text>
            </TouchableOpacity>
          )}
          {/* Save button */}
          <TouchableOpacity
            onPress={() => formSubmitRef.current?.triggerSubmit()}
            disabled={loading}
            style={[
              styles.headerButton,
              styles.headerButtonSave,
              { backgroundColor: loading ? theme.colors.border : theme.colors.primary },
            ]}
            activeOpacity={0.7}
          >
            <Text style={styles.headerButtonText}>
              {loading
                ? t('common.saving', { defaultValue: 'Saving...' })
                : t('common.save', { defaultValue: 'Save' })}
            </Text>
          </TouchableOpacity>
        </View>
      ),
    });
  }, [loading, isEditMode, theme, t, handleDelete]);

  // Handle back button with unsaved changes warning
  useFocusEffect(
    useCallback(() => {
      const unsubscribe = navigation.addListener('beforeRemove', (e) => {
        if (!hasUnsavedChanges || loading) {
          return; // Allow navigation if no changes or currently loading
        }

        // Prevent default behavior
        e.preventDefault();

        // Show confirmation dialog
        showConfirm(
          t('portfolio.unsavedChanges', { defaultValue: 'Unsaved Changes' }),
          t('portfolio.unsavedChangesMessage', {
            defaultValue: 'You have unsaved changes. Exit without saving?',
          }),
          t('common.exit', { defaultValue: 'Exit' }),
          t('common.stay', { defaultValue: 'Stay' })
        ).then((confirmed) => {
          if (confirmed) {
            // User chose to exit - navigate back without saving
            navigation.dispatch(e.data.action);
          }
          // If not confirmed, stay on current screen (do nothing)
        });
      });

      return unsubscribe;
    }, [hasUnsavedChanges, loading, navigation, t])
  );

  const handleSubmit = async (
    symbol: string,
    quantity: number,
    cost_price: number,
    close_price?: number | null,
    currency: string,
    asset_type?: string,
    sector?: string | null,
    industry?: string | null
  ) => {
    // Store current values for duplicate dialog
    setCurrentAssetCategory(asset_type || 'Stocks');
    setCurrentSector(sector || '');
    setCurrentIndustry(industry || '');

    setLoading(true);

    try {
      // T041 [US2]: Edit mode - update existing position
      if (isEditMode && positionId) {
        await updatePosition.mutateAsync({
          positionId,
          params: {
            quantity,
            cost_price,
            close_price,
            asset_type,
            sector,
            industry,
          },
        });
        showSuccess(
          t('portfolio.positionUpdated', { defaultValue: 'Position updated successfully!' })
        );
        setHasUnsavedChanges(false);
      } else {
        // Add mode - create new position
        console.log('[handleSubmit] Creating new position with quantity:', quantity);
        const result = await createPosition.mutateAsync({
          symbol: symbol.toUpperCase(),
          quantity,
          cost_price,
          currency,
          asset_type,
          sector,
          industry,
          merge_with_existing: false, // First attempt without merge
        });
        console.log('[handleSubmit] Position created successfully, result:', result);
        showSuccess(t('portfolio.positionAdded', { defaultValue: 'Position added successfully!' }));
        setHasUnsavedChanges(false);

        // New positions are now trade-backed server-side (an opening deposit is
        // created by POST /positions), so no client-side reconcile step is needed.
        console.log('[handleSubmit] New position created (trade-backed), navigating back');
      }
      navigation.goBack();
    } catch (error: any) {
      // Handle quantity mismatch error (both add and edit mode)
      const detail = error.response?.data?.detail || error.response?.data;
      if (detail?.error === 'quantity_mismatch') {
        console.log('[handleSubmit] Quantity mismatch error:', detail);

        // Set state for reconcile modal
        setReconcileDialog({
          visible: true,
          reconcileData: {
            position_id: positionId!,
            symbol: position?.symbol || '',
            current_quantity: detail.current_quantity,
            target_quantity: detail.target_quantity,
            difference: detail.difference,
            suggested_trade: detail.suggested_trade,
          },
        });
        return;
      }

      // T041 [US2]: Only handle duplicate detection in add mode
      if (!isEditMode) {
        // Handle duplicate symbol/instrument errors
        if (error.code === 'DUPLICATE_SYMBOL') {
          const extractedInstrumentId = error.instrumentId || error.existingPosition?.instrument_id;

          console.log('[handleSubmit] DUPLICATE_SYMBOL error, setting dialog:', {
            existingPosition: error.existingPosition,
            instrumentId: extractedInstrumentId,
            formData: { symbol, quantity, cost_price, currency },
          });

          setDuplicateDialog({
            visible: true,
            existingPosition: error.existingPosition,
            formData: { symbol, quantity, cost_price, currency, asset_type, sector, industry },
            duplicateType: 'instrument',
            instrumentId: extractedInstrumentId,
          });
          return;
        }

        // Also handle errors from direct API calls (with response.data.detail)
        if (detail?.error === 'duplicate_instrument' || detail?.error === 'duplicate_symbol') {
          const existingPos = detail?.existing_position;
          const extractedInstrumentId = detail?.instrument_id || existingPos?.instrument_id;

          console.log('[handleSubmit] duplicate instrument from detail:', {
            existingPosition: existingPos,
            instrumentId: extractedInstrumentId,
          });

          setDuplicateDialog({
            visible: true,
            existingPosition: existingPos,
            formData: { symbol, quantity, cost_price, currency, asset_type, sector, industry },
            duplicateType: 'instrument',
            instrumentId: extractedInstrumentId,
          });
          return;
        }
      }
      showError(error.message || `Failed to ${isEditMode ? 'update' : 'add'} position`);
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (!duplicateDialog.formData) return;

    setLoading(true);
    setDuplicateDialog({ ...duplicateDialog, visible: false });

    try {
      console.log('[handleMerge] Duplicate dialog state:', {
        instrumentId: duplicateDialog.instrumentId,
        existingPositionId: duplicateDialog.existingPosition?.id,
        formData: duplicateDialog.formData,
      });

      if (!duplicateDialog.instrumentId || !duplicateDialog.existingPosition) {
        showError('Missing instrument information. Please try again.');
        return;
      }

      // Use the link-with-resolution API for all duplicate positions
      const token = await getToken();
      console.log('[handleMerge] Token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');

      const requestData = {
        instrument_id: duplicateDialog.instrumentId,
        existing_position_id: duplicateDialog.existingPosition.id,
        new_position_symbol: duplicateDialog.formData.symbol.toUpperCase(),
        new_position_quantity: duplicateDialog.formData.quantity,
        new_position_cost: duplicateDialog.formData.cost_price,
        action: 'add', // Merge with existing
        user_id: userId,
      };
      console.log('[handleMerge] Request data:', requestData);

      await axios.post(
        `${API_URL}/api/v1/financial-data/instruments/link-with-resolution`,
        {
          instrument_id: duplicateDialog.instrumentId,
          existing_position_id: duplicateDialog.existingPosition.id,
          new_position_symbol: duplicateDialog.formData.symbol.toUpperCase(),
          new_position_quantity: duplicateDialog.formData.quantity,
          new_position_cost: duplicateDialog.formData.cost_price,
          action: 'add', // Merge with existing
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccess('Position merged successfully!');
      navigation.goBack();
    } catch (error: any) {
      console.error('[handleMerge] Error:', error);
      showError(error.message || 'Failed to merge position');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSeparate = async () => {
    if (!duplicateDialog.formData) return;

    setLoading(true);
    setDuplicateDialog({ ...duplicateDialog, visible: false });

    try {
      console.log('[handleCreateSeparate] Duplicate dialog state:', {
        instrumentId: duplicateDialog.instrumentId,
        existingPositionId: duplicateDialog.existingPosition?.id,
        formData: duplicateDialog.formData,
      });

      if (!duplicateDialog.instrumentId || !duplicateDialog.existingPosition) {
        showError('Missing instrument information. Please try again.');
        return;
      }

      // Use the link-with-resolution API for all duplicate positions
      const token = await getToken();

      await axios.post(
        `${API_URL}/api/v1/financial-data/instruments/link-with-resolution`,
        {
          instrument_id: duplicateDialog.instrumentId,
          existing_position_id: duplicateDialog.existingPosition.id,
          new_position_symbol: duplicateDialog.formData.symbol.toUpperCase(),
          new_position_quantity: duplicateDialog.formData.quantity,
          new_position_cost: duplicateDialog.formData.cost_price,
          action: 'replace', // Replace existing
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccess('Position added successfully!');
      navigation.goBack();
    } catch (error: any) {
      console.error('[handleCreateSeparate] Error:', error);
      showError(error.message || 'Failed to add position');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDialog = () => {
    setDuplicateDialog({ ...duplicateDialog, visible: false });
  };

  const handleCancelReconcile = () => {
    setReconcileDialog({ visible: false, reconcileData: null });
  };

  const handleAddReconcileTrade = async (tradeData: {
    transaction_type: string;
    quantity: number;
    trade_price: number;
    trade_datetime: string;
    comment?: string;
  }) => {
    // Add the reconciling trade to the position
    setLoading(true);
    setReconcileDialog({ visible: false, reconcileData: null });

    try {
      const token = await getToken();
      const trade = {
        symbol: reconcileDialog.reconcileData?.symbol || position?.symbol || '',
        trade_datetime: tradeData.trade_datetime,
        quantity: tradeData.quantity,
        trade_price: tradeData.trade_price,
        transaction_type: tradeData.transaction_type,
        currency: reconcileDialog.reconcileData?.currency || position?.currency || 'USD',
        commission_fee: 0,
        comment: tradeData.comment,
      };

      await axios.post(`${API_URL}/api/v1/portfolio/trades`, trade, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showSuccess(t('portfolio.positionUpdated', { defaultValue: 'Trade added successfully!' }));
      setHasUnsavedChanges(false);
      navigation.goBack();
    } catch (error: any) {
      console.error('[handleAddReconcileTrade] Error:', error);
      showError(error.message || 'Failed to add reconciling trade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        {/* T041 [US2]: Show loading state when fetching position data */}
        {isLoadingPosition ? (
          <View style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
              {t('common.loading', { defaultValue: 'Loading...' })}
            </Text>
          </View>
        ) : (
          <PositionForm
            onSubmit={handleSubmit}
            onFormChange={(hasChanges) => setHasUnsavedChanges(hasChanges)}
            submitRef={formSubmitRef}
            loading={loading}
            initialValues={
              isEditMode && position
                ? {
                    symbol: position.symbol,
                    quantity: position.quantity,
                    cost_price: position.cost_price,
                    close_price: position.close_price,
                    currency: position.currency,
                    asset_type: position.asset_type,
                    // Use position's own sector/industry (may be null if linked)
                    sector: position.sector,
                    industry: position.industry,
                    // Pass instrument values for reference (for placeholder hints)
                    instrument_sector: position.instrument_sector,
                    instrument_industry: position.instrument_industry,
                  }
                : {
                    symbol: undefined,
                    quantity: undefined,
                    cost_price: undefined,
                    close_price: undefined,
                    currency: 'USD',
                    asset_type: 'Stocks',
                    sector: undefined,
                    industry: undefined,
                  }
            }
            readonlySymbol={isEditMode} // Prevent symbol changes in edit mode
            readonlyFields={position?.instrument_id ? ['asset_type', 'currency'] : []}
            hasInstrumentLink={position?.instrument_id != null}
          />
        )}

        {/* Duplicate Instrument Dialog - shown for all duplicate positions (same symbol or same instrument) */}
        {duplicateDialog.visible &&
          duplicateDialog.existingPosition &&
          duplicateDialog.formData && (
            <DuplicateInstrumentDialog
              visible={duplicateDialog.visible}
              existingPosition={duplicateDialog.existingPosition}
              newPosition={{
                symbol: duplicateDialog.formData.symbol,
                quantity: duplicateDialog.formData.quantity,
                cost_price: duplicateDialog.formData.cost_price,
              }}
              onAdd={handleMerge}
              onReplace={handleCreateSeparate}
              onCancel={handleCancelDialog}
              loading={loading}
            />
          )}

        {/* Position Reconcile Dialog - shown when position quantity doesn't match trades */}
        <PositionReconcileModal
          visible={reconcileDialog.visible}
          reconcileData={reconcileDialog.reconcileData}
          onConfirm={handleAddReconcileTrade}
          onCancel={handleCancelReconcile}
          loading={loading}
        />
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    scrollContent: {
      padding: 16,
    },
    headerButtons: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    headerButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 50,
    },
    headerButtonSave: {
      backgroundColor: theme.colors.primary,
      marginRight: 8, // Right padding for save button
    },
    headerButtonDelete: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: theme.colors.error,
    },
    headerButtonText: {
      color: theme.colors.textInverse,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    headerButtonTextDelete: {
      color: theme.colors.error,
    },
    dialogOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
    },
    dialogContainer: {
      borderRadius: theme.radii.lg,
      padding: 24,
      width: '100%',
      maxWidth: 400,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    dialogTitle: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: 16,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    dialogMessage: {
      fontSize: theme.typography.fontSizeMd,
      lineHeight: 24,
      marginBottom: 24,
    },
    dialogButton: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      marginBottom: 12,
      minHeight: 44,
      justifyContent: 'center',
    },
    dialogButtonText: {
      color: theme.colors.textInverse,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    dialogButtonTextCancel: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    loadingContainer: {
      borderRadius: theme.radii.lg,
      padding: 32,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    loadingText: {
      marginTop: 16,
      fontSize: theme.typography.fontSizeMd,
    },
  });
}
